import { PermissionCode } from '@gb-ticket/shared-types';
import { type FormEvent, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ApiRequestError } from '@/lib/api';
import { hasPermission } from '@/platform/auth/permissions';
import { useMe } from '@/platform/auth/use-me';
import {
  FLEET_ODOMETER_SOURCE_LABELS,
  formatDeDateTime,
} from '@/products/fleet/features/fleet-labels';
import {
  fleetPanelClassName,
  fleetTableFrameClassName,
} from '@/products/fleet/features/fleet-panel';
import {
  useCorrectFleetOdometer,
  useFleetOdometer,
  useRecordFleetOdometer,
} from '@/products/fleet/features/use-fleet';

export function FleetVehicleOdometerTab({ vehicleId }: { vehicleId: string }) {
  const me = useMe();
  const canCorrect = hasPermission(me.data, PermissionCode.FLEET_ODOMETER_CORRECT);
  const canWrite =
    hasPermission(me.data, PermissionCode.FLEET_MILEAGE_WRITE) ||
    hasPermission(me.data, PermissionCode.FLEET_VEHICLES_MANAGE);
  const odometer = useFleetOdometer(vehicleId);
  const record = useRecordFleetOdometer(vehicleId);
  const correct = useCorrectFleetOdometer(vehicleId);
  const [readingKm, setReadingKm] = useState('');
  const [note, setNote] = useState('');
  const [correctionKm, setCorrectionKm] = useState('');
  const [correctionNote, setCorrectionNote] = useState('');

  return (
    <div className="space-y-6">
      {odometer.isPending ? (
        <p className="text-sm text-muted-foreground">Kilometerstände werden geladen …</p>
      ) : (
        <p className="text-sm text-muted-foreground">
          Aktueller Stand:{' '}
          {odometer.data?.currentKm != null
            ? `${odometer.data.currentKm.toLocaleString('de-DE')} km`
            : 'noch keiner'}
        </p>
      )}
      {odometer.isPending ? null : odometer.data && odometer.data.items.length > 0 ? (
        <div className={fleetTableFrameClassName}>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Datum</TableHead>
                <TableHead>Stand</TableHead>
                <TableHead>Quelle</TableHead>
                <TableHead>Person</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {odometer.data.items.map((row) => (
                <TableRow key={row.id}>
                  <TableCell>{formatDeDateTime(row.recordedAt)}</TableCell>
                  <TableCell>
                    {row.readingKm.toLocaleString('de-DE')} km
                    {row.warning ? (
                      <span className="mt-1 block text-amber-700 dark:text-amber-400">
                        {row.warning}
                      </span>
                    ) : null}
                  </TableCell>
                  <TableCell>{FLEET_ODOMETER_SOURCE_LABELS[row.source]}</TableCell>
                  <TableCell>{row.recordedByName ?? '—'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">Noch keine Kilometerstände.</p>
      )}

      {canWrite ? (
        <form
          className={`${fleetPanelClassName} space-y-3`}
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void record
              .mutateAsync({
                readingKm: Number(readingKm),
                note: note.trim() || undefined,
              })
              .then((result) => {
                setReadingKm('');
                setNote('');
                toast.success('Kilometerstand gespeichert.', {
                  description: result.warning ?? undefined,
                });
              })
              .catch((error: unknown) => {
                toast.error(
                  error instanceof ApiRequestError ? error.message : 'Speichern fehlgeschlagen.',
                );
              });
          }}
        >
          <h3 className="text-base font-semibold">Stand melden</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="tab-odo-km">Kilometer</Label>
              <Input
                id="tab-odo-km"
                type="number"
                min={0}
                value={readingKm}
                onChange={(event) => setReadingKm(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tab-odo-note">Notiz</Label>
              <Input
                id="tab-odo-note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
              />
            </div>
          </div>
          <Button type="submit" disabled={record.isPending}>
            Melden
          </Button>
        </form>
      ) : null}

      {canCorrect ? (
        <form
          className={`${fleetPanelClassName} space-y-3`}
          onSubmit={(event: FormEvent) => {
            event.preventDefault();
            void correct
              .mutateAsync({
                readingKm: Number(correctionKm),
                note: correctionNote.trim(),
              })
              .then(() => {
                setCorrectionKm('');
                setCorrectionNote('');
                toast.success('Korrektur gespeichert.');
              })
              .catch((error: unknown) => {
                toast.error(
                  error instanceof ApiRequestError ? error.message : 'Korrektur fehlgeschlagen.',
                );
              });
          }}
        >
          <h3 className="text-base font-semibold">Korrektur</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="tab-corr-km">Kilometer</Label>
              <Input
                id="tab-corr-km"
                type="number"
                min={0}
                value={correctionKm}
                onChange={(event) => setCorrectionKm(event.target.value)}
                required
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tab-corr-note">Notiz (Pflicht)</Label>
              <Input
                id="tab-corr-note"
                value={correctionNote}
                onChange={(event) => setCorrectionNote(event.target.value)}
                required
              />
            </div>
          </div>
          <Button type="submit" variant="outline" disabled={correct.isPending}>
            Korrigieren
          </Button>
        </form>
      ) : null}
    </div>
  );
}
